<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppButton from '@/components/ui/AppButton/AppButton'
import {
  MOVE_BACKWARD_BUTTON_TEST_ID,
  MOVE_FORWARD_BUTTON_TEST_ID,
  RESET_BUTTON_TEST_ID
} from '@/components/ui/AppDatesNavigation/jest-helpers'

defineOptions({
  name: 'AppDatesNavigation'
})

const props = defineProps({
  showResetButton: {
    type: Boolean,
    default: true
  },

  disabled: {
    type: Boolean
  },

  isBackwardDisabled: {
    type: Boolean
  },

  isForwardDisabled: {
    type: Boolean
  },

  isResetDisabled: {
    type: Boolean
  },

  resetButtonText: {
    type: String,
    default: ''
  }
})

const { t } = useI18n()

const emit = defineEmits({
  'move-to-startpoint': null,
  'move-forward': null,
  'move-backward': null
})

const moveBackward = () => emit('move-backward')
const moveForward = () => emit('move-forward')
const reset = () => emit('move-to-startpoint')

const navActions = computed(() => {
  return [
    {
      name: 'backward',
      icon: 'chevron-left-next',
      handler: moveBackward,
      disabled: props.isBackwardDisabled,
      testId: MOVE_BACKWARD_BUTTON_TEST_ID
    },
    {
      shouldBeHidden: !props.showResetButton,
      name: 'reset',
      text: props.resetButtonText || t('common.reset'),
      handler: reset,
      disabled: props.isResetDisabled,
      testId: RESET_BUTTON_TEST_ID
    },
    {
      name: 'forward',
      icon: 'chevron-right-next',
      handler: moveForward,
      disabled: props.isForwardDisabled,
      testId: MOVE_FORWARD_BUTTON_TEST_ID
    }
  ].filter(action => !action.shouldBeHidden)
})
</script>

<template>
  <div class="adn-Navigation">
    <AppButton
      v-for="action in navActions"
      :key="action.name"
      :class="{
        [`adn-Navigation_Button-${action.name}`]: true,
        'adn-Navigation_Button-disabled': disabled || action.disabled
      }"
      :data-auto-testid="action.testId"
      :data-testid="action.testId"
      :disable="disabled || action.disabled"
      :icon="action.icon"
      :remove-padding="!action.text"
      class="adn-Navigation_Button"
      type="tertiary-next"
      @click="action.handler"
    >
      <template v-if="action.text">
        {{ action.text }}
      </template>
    </AppButton>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/mind-road-map-footer';

.adn-Navigation {
  @extend %buttons-group-styles;
}

.adn-Navigation_Button {
  min-width: 32px;
  background: $grey-3-next;
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;

  &:not(&-disabled) {
    @include hoverState($grey-2-next, 1%);
    @include activeState($grey-3-next, 5%);
  }

  &-reset {
    padding: 0 8px;
  }
}
</style>
